import { useEffect, useState } from "react";
import { Mail, Phone, MapPin, Clock, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useSettings } from "../context/SettingsContext";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";

export default function Contact() {
  const { general, whatsapp } = useSettings();
  const [page, setPage] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { api.get("/pages/contact").then((r) => setPage(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) { toast.error("Please fill in all required fields"); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      toast.success("Message sent! We'll get back to you soon.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally { setSending(false); }
  };

  const info = [
    { icon: Mail, label: "Email", value: general.contact_email, href: `mailto:${general.contact_email}` },
    { icon: Phone, label: "Phone", value: general.phone, href: general.phone ? `tel:${general.phone}` : null },
    { icon: MapPin, label: "Address", value: general.address },
    { icon: Clock, label: "Business hours", value: general.business_hours },
  ].filter((i) => i.value);

  const waHref = whatsapp?.number ? `https://wa.me/${whatsapp.number.replace(/[^0-9]/g, "")}` : null;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <PublicHeader />
      <section className="relative bg-slate-900 py-24" data-testid="contact-hero">
        {page?.featured_image && (
          <>
            <div className="absolute inset-0" style={{ backgroundImage: `url(${page.featured_image})`, backgroundSize: "cover", backgroundPosition: "center" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(15,23,42,0.94), rgba(15,23,42,0.78))" }} />
          </>
        )}
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-white">{page?.hero_title || "Contact Us"}</h1>
          {page?.hero_subtitle && <p className="text-lg text-slate-200 mt-5 max-w-2xl leading-relaxed">{page.hero_subtitle}</p>}
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
          {/* Info */}
          <div className="lg:col-span-2 space-y-6">
            {(page?.blocks || []).map((b, i) => (
              <div key={i}>
                <h2 className="font-display text-xl font-bold text-slate-900">{b.heading}</h2>
                <p className="text-slate-600 mt-2 leading-relaxed">{b.body}</p>
              </div>
            ))}
            <div className="space-y-4" data-testid="contact-info">
              {info.map((it) => (
                <div key={it.label} className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-lg bg-blue-50 grid place-items-center text-blue-600 shrink-0"><it.icon className="h-5 w-5" /></div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">{it.label}</div>
                    {it.href ? <a href={it.href} className="text-slate-800 hover:text-blue-600 font-medium">{it.value}</a> : <div className="text-slate-800 font-medium">{it.value}</div>}
                  </div>
                </div>
              ))}
              {waHref && (
                <a href={waHref} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 group">
                  <div className="h-10 w-10 rounded-lg bg-[#25D366]/10 grid place-items-center text-[#25D366] shrink-0"><MessageCircle className="h-5 w-5" /></div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">WhatsApp</div>
                    <div className="text-slate-800 group-hover:text-[#25D366] font-medium">{whatsapp.number}</div>
                  </div>
                </a>
              )}
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 lg:p-8" data-testid="contact-form">
              {sent ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <div className="h-14 w-14 rounded-full bg-emerald-50 grid place-items-center text-emerald-500 mx-auto"><Send className="h-6 w-6" /></div>
                  <h3 className="font-display text-xl font-bold text-slate-900 mt-4">Message sent</h3>
                  <p className="text-slate-600 mt-2">Thanks for reaching out. Our team will get back to you shortly.</p>
                  <Button onClick={() => setSent(false)} variant="outline" className="mt-6 border-slate-300 text-slate-700">Send another message</Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5"><Label className="text-slate-700">Name *</Label><Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-slate-300 text-slate-900" placeholder="Your name" /></div>
                    <div className="space-y-1.5"><Label className="text-slate-700">Email *</Label><Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white border-slate-300 text-slate-900" placeholder="you@example.com" /></div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5"><Label className="text-slate-700">Phone</Label><Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white border-slate-300 text-slate-900" placeholder="Optional" /></div>
                    <div className="space-y-1.5"><Label className="text-slate-700">Subject *</Label><Input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="bg-white border-slate-300 text-slate-900" placeholder="How can we help?" /></div>
                  </div>
                  <div className="space-y-1.5"><Label className="text-slate-700">Message *</Label><Textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-white border-slate-300 text-slate-900 min-h-[140px]" placeholder="Tell us more…" /></div>
                  <Button type="submit" disabled={sending} data-testid="contact-submit" className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-2">{sending ? "Sending…" : <>Send message <Send className="h-4 w-4" /></>}</Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
